import { Component, ElementRef, Input, NgZone, OnChanges, OnDestroy, PLATFORM_ID, ViewChild, inject } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FeaturedProductCardComponent } from '../featured-product-card/featured-product-card.component';

/** Marketing product carousel (Wireframe_v2 "Featured & for you").
 *  4-up window of equal cards; auto-advances 1 card / 7s with a 350ms ease-out slide;
 *  pauses on hover; seamless loop. Navigation is either the dots, or prev()/next()
 *  called from the host page's own arrows (showDots=false). Shared by the homepage
 *  Featured and Recently Bought sections so they're identical. */
@Component({
  selector: 'app-featured-carousel',
  standalone: true,
  imports: [CommonModule, FeaturedProductCardComponent],
  templateUrl: './featured-carousel.component.html',
  styleUrl: './featured-carousel.component.css',
})
export class FeaturedCarouselComponent implements OnChanges, OnDestroy {
  private platformId = inject(PLATFORM_ID);
  private zone = inject(NgZone);
  @Input() items: any[] = [];
  /** Passed to the cards: decides the "Log in for your pricing" line. */
  @Input() signedIn = false;
  @Input() showDots = true;

  @ViewChild('fpTrack') private fpTrackRef?: ElementRef<HTMLElement>;
  fpItems: any[] = [];   // items repeated 3x for seamless looping
  fpPos = 0;             // current left-slot step index
  fpAnim = true;         // whether the slide transition is enabled
  fpStride = 0;          // px distance of one card step (measured at runtime)
  fpN = 0;               // number of source items
  private fpTimer: any;
  private resizeHandler = () => this.measureFpStride();

  ngOnChanges(): void {
    this.buildFpItems();
    this.startAuto();
    if (isPlatformBrowser(this.platformId)) {
      window.removeEventListener('resize', this.resizeHandler);
      window.addEventListener('resize', this.resizeHandler, { passive: true });
    }
  }

  ngOnDestroy(): void {
    this.stopAuto();
    if (isPlatformBrowser(this.platformId)) {
      window.removeEventListener('resize', this.resizeHandler);
    }
  }

  private buildFpItems(): void {
    const p = this.items || [];
    this.fpN = p.length;
    // repeat 3x so the 4-card window is always filled through one full loop
    this.fpItems = p.length ? [...p, ...p, ...p] : [];
    this.fpPos = 0;
  }

  /** Distance (px) of one card step = slot width + gap. Measured from the DOM so it
   *  stays correct across breakpoints. */
  measureFpStride(): void {
    const track = this.fpTrackRef?.nativeElement;
    if (!track || track.children.length < 2) return;
    const a = (track.children[0] as HTMLElement).getBoundingClientRect();
    const b = (track.children[1] as HTMLElement).getBoundingClientRect();
    const stride = Math.round(b.left - a.left);
    if (stride > 0) this.fpStride = stride;
  }

  /** The interval runs OUTSIDE the Angular zone: zone.js treats a periodic timer as a
   *  task that never completes, so ApplicationRef.isStable() would never emit true and
   *  hydration would never finish (NG0506) — Angular then gives up and re-renders the
   *  whole app, which reads as the page loading a second time. Re-entering the zone for
   *  the tick itself keeps change detection working exactly as before. */
  startAuto(): void {
    if (!isPlatformBrowser(this.platformId) || this.fpN <= 1) return;
    this.stopAuto();
    this.zone.runOutsideAngular(() => {
      this.fpTimer = setInterval(() => this.zone.run(() => this.fpNext()), 7000);
    });
  }

  stopAuto(): void {
    if (this.fpTimer) { clearInterval(this.fpTimer); this.fpTimer = null; }
  }

  /** Advance one card (Figma: step 1, 350ms ease-out). Loops seamlessly. */
  fpNext(): void {
    this.measureFpStride();
    this.fpAnim = true;
    this.fpPos++;
  }

  /** After the slide finishes past the last item, jump back with no transition so the
   *  loop is seamless (the duplicated items look identical at pos 0). */
  onFpTransitionEnd(e: TransitionEvent): void {
    if (e.propertyName !== 'transform') return;
    if (this.fpN > 0 && this.fpPos >= this.fpN) {
      this.fpAnim = false;
      this.fpPos -= this.fpN;
      if (isPlatformBrowser(this.platformId)) {
        requestAnimationFrame(() => (this.fpAnim = true));
      } else {
        this.fpAnim = true;
      }
    }
  }

  /** Arrow controls for the host page; both restart autoplay. */
  next(): void {
    this.fpNext();
    this.startAuto();
  }

  /** Back one card. From the first position, jump (no transition) to the identical
   *  copy one set ahead, then slide back from there, so the loop stays seamless. */
  prev(): void {
    if (this.fpN <= 1) return;
    this.measureFpStride();
    if (this.fpPos === 0) {
      this.fpAnim = false;
      this.fpPos = this.fpN;
      requestAnimationFrame(() => requestAnimationFrame(() => {
        this.fpAnim = true;
        this.fpPos--;
      }));
    } else {
      this.fpAnim = true;
      this.fpPos--;
    }
    this.startAuto();
  }

  /** Manual dot jump; keeps the same step behavior and restarts autoplay. */
  goFp(i: number): void {
    this.measureFpStride();
    this.fpAnim = true;
    this.fpPos = i;
    this.startAuto();
  }
}
